import React from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import {
  FeedbackGroup,
  QuestionBlank,
  QuestionNode,
  QuestionOption,
} from "../../models";

const QUESTION_TYPES = [
  "MULTIPLE_CHOICE",
  "MULTIPLE_CORRECT",
  "RANK",
  "FILL_IN_BLANK",
];

interface Props {
  node: QuestionNode;
  index: number;
  totalCount: number;
  expanded: boolean;
  feedbackGroups: FeedbackGroup[];
  onToggle: () => void;
  onChange: (updated: QuestionNode) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

export const QuestionCard: React.FC<Props> = ({
  node,
  index,
  totalCount,
  expanded,
  feedbackGroups,
  onToggle,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
}) => {
  const updateField = (field: string, value: any) => {
    onChange({ ...node, [field]: value });
  };

  const updateOption = (optIndex: number, updates: Partial<QuestionOption>) => {
    const newOptions = node.options.map((o, i) =>
      i === optIndex ? { ...o, ...updates } : o
    );
    // For MULTIPLE_CHOICE, ensure only one correct
    if (updates.is_correct && node.question_type === "MULTIPLE_CHOICE") {
      newOptions.forEach((o, i) => {
        if (i !== optIndex) o.is_correct = false;
      });
    }
    onChange({ ...node, options: newOptions });
  };

  const addOption = () => {
    const newOpt: QuestionOption = {
      id: `o-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      text: "",
      is_correct: false,
    };
    onChange({ ...node, options: [...node.options, newOpt] });
  };

  const removeOption = (optIndex: number) => {
    onChange({
      ...node,
      options: node.options.filter((_, i) => i !== optIndex),
    });
  };

  const handleInsertBlank = () => {
    const blankCount = (node.blanks ?? []).length;
    const blankId = `BLANK${blankCount + 1}`;
    const newText = node.question_text + ` [[${blankId}]]`;
    const newBlanks: QuestionBlank[] = [
      ...(node.blanks ?? []),
      { id: blankId, correct_option_id: node.options[0]?.id ?? "" },
    ];
    onChange({ ...node, question_text: newText, blanks: newBlanks });
  };

  const updateBlank = (blankIndex: number, correctOptionId: string) => {
    const newBlanks = (node.blanks ?? []).map((b, i) =>
      i === blankIndex ? { ...b, correct_option_id: correctOptionId } : b
    );
    onChange({ ...node, blanks: newBlanks });
  };

  const questionPlaceholder: Record<string, string> = {
    MULTIPLE_CHOICE: "e.g., Credit is mostly about?",
    MULTIPLE_CORRECT: "e.g., Which of these are types of credit?",
    RANK: "e.g., Order these factors by impact on credit score",
    FILL_IN_BLANK:
      "e.g., Credit is borrowing money now and paying it back [[BLANK1]]",
  };

  const optionExamples = [
    "e.g., Trust",
    "e.g., Speed",
    "e.g., Luck",
    "e.g., Gifts",
  ];

  // Preview text (truncated)
  const preview =
    node.question_text.length > 80
      ? node.question_text.slice(0, 80) + "..."
      : node.question_text || "(empty question)";

  const correctFgs = feedbackGroups.filter((fg) => fg.type === "CORRECT");
  const incorrectFgs = feedbackGroups.filter((fg) => fg.type === "INCORRECT");

  return (
    <Card sx={{ mb: 1 }}>
      <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
        {/* Collapsed header */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mr: 1, minWidth: 24 }}
          >
            {index + 1}.
          </Typography>
          <IconButton size="small" onClick={onToggle}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              ml: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
            onClick={onToggle}
          >
            {preview}
          </Typography>
          <Chip
            label={node.question_type.replace(/_/g, " ")}
            size="small"
            variant="outlined"
            sx={{ mr: 1 }}
          />
          <Chip
            label={`${node.options.length} opts`}
            size="small"
            variant="outlined"
            sx={{ mr: 1 }}
          />
          <IconButton size="small" onClick={onMoveUp} disabled={index === 0}>
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onMoveDown}
            disabled={index === totalCount - 1}
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={onDelete}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Expanded editing form */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, pl: 4 }}>
            {/* Question text */}
            <TextField
              label="Question Text"
              value={node.question_text}
              onChange={(e) => updateField("question_text", e.target.value)}
              fullWidth
              multiline
              rows={3}
              size="small"
              sx={{ mb: 2 }}
              placeholder={
                questionPlaceholder[node.question_type] ??
                "e.g., Credit is mostly about?"
              }
            />

            {/* Question type */}
            <TextField
              label="Question Type"
              value={node.question_type}
              onChange={(e) => updateField("question_type", e.target.value)}
              select
              size="small"
              sx={{ mb: 1, minWidth: 200 }}
            >
              {QUESTION_TYPES.map((qt) => (
                <MenuItem key={qt} value={qt}>
                  {qt.replace(/_/g, " ")}
                </MenuItem>
              ))}
            </TextField>
            <Alert
              severity="info"
              variant="outlined"
              sx={{ mb: 2 }}
              icon={false}
            >
              <Typography variant="caption">
                {node.question_type === "MULTIPLE_CHOICE" &&
                  "User sees the question and picks one answer. Only one option can be correct (radio buttons). After answering, the option's explanation is shown."}
                {node.question_type === "MULTIPLE_CORRECT" &&
                  "User sees the question and must select all correct answers (checkboxes). Multiple options can be marked correct. After answering, explanations for selected options are shown."}
                {node.question_type === "RANK" &&
                  "User drags options into the correct order. The order you define here (top to bottom) is the correct order. No option is marked correct/incorrect — correctness is based on matching the exact order."}
                {node.question_type === "FILL_IN_BLANK" &&
                  'Question text contains [[BLANK1]], [[BLANK2]], etc. placeholders. Each blank maps to one correct option from the options list. User taps an option to fill each blank. Use "Insert Blank" above to add placeholders.'}
              </Typography>
            </Alert>

            {/* Fill in blank: Insert Blank button */}
            {node.question_type === "FILL_IN_BLANK" && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label="Insert Blank"
                  onClick={handleInsertBlank}
                  icon={<AddIcon />}
                  clickable
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}

            {/* Options */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Options
            </Typography>
            {(node.question_type === "MULTIPLE_CHOICE" ||
              node.question_type === "MULTIPLE_CORRECT") && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: "block" }}
              >
                {node.question_type === "MULTIPLE_CHOICE"
                  ? "Select the one correct answer"
                  : "Check all correct answers"}
              </Typography>
            )}
            {node.options.map((opt, optIdx) => (
              <Box key={opt.id} sx={{ mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {node.question_type === "MULTIPLE_CHOICE" && (
                    <Tooltip title="Correct answer" arrow>
                      <Radio
                        checked={opt.is_correct === true}
                        onChange={() =>
                          updateOption(optIdx, { is_correct: true })
                        }
                        size="small"
                        color="success"
                      />
                    </Tooltip>
                  )}
                  {node.question_type === "MULTIPLE_CORRECT" && (
                    <Tooltip title="Correct answer" arrow>
                      <Checkbox
                        checked={opt.is_correct === true}
                        onChange={(e) =>
                          updateOption(optIdx, {
                            is_correct: e.target.checked,
                          })
                        }
                        size="small"
                        color="success"
                      />
                    </Tooltip>
                  )}
                  {node.question_type === "RANK" && (
                    <Typography
                      variant="body2"
                      sx={{ minWidth: 24, textAlign: "center" }}
                    >
                      {optIdx + 1}.
                    </Typography>
                  )}
                  <TextField
                    value={opt.text}
                    onChange={(e) =>
                      updateOption(optIdx, { text: e.target.value })
                    }
                    fullWidth
                    size="small"
                    placeholder={
                      optionExamples[optIdx] ?? `Option ${optIdx + 1}`
                    }
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeOption(optIdx)}
                  >
                    <RemoveCircleIcon fontSize="small" />
                  </IconButton>
                </Box>
                <TextField
                  value={opt.explanation ?? ""}
                  onChange={(e) =>
                    updateOption(optIdx, {
                      explanation: e.target.value || undefined,
                    })
                  }
                  fullWidth
                  size="small"
                  placeholder="e.g., Correct: Credit measures whether lenders trust you to pay money back."
                  sx={{
                    mt: 0.5,
                    ml: node.question_type === "RANK" ? "32px" : "42px",
                  }}
                  InputProps={{
                    sx: { fontSize: "0.85rem" },
                  }}
                />
                <FormControl
                  size="small"
                  sx={{
                    mt: 0.5,
                    ml: node.question_type === "RANK" ? "32px" : "42px",
                    minWidth: 200,
                  }}
                >
                  <InputLabel>Option Feedback</InputLabel>
                  <Select
                    value={opt.feedback_group_id ?? ""}
                    label="Option Feedback"
                    onChange={(e) =>
                      updateOption(optIdx, {
                        feedback_group_id:
                          (e.target.value as string) || undefined,
                      })
                    }
                    sx={{ fontSize: "0.85rem" }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {feedbackGroups.map((fg) => (
                      <MenuItem key={fg.id} value={fg.id}>
                        {fg.id} ({fg.type.toLowerCase()})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ))}
            <Chip
              label="Add Option"
              onClick={addOption}
              icon={<AddIcon />}
              clickable
              size="small"
              sx={{ mb: 2 }}
            />

            {/* Fill in blank: Blank mappings */}
            {node.question_type === "FILL_IN_BLANK" &&
              (node.blanks ?? []).length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Blank Mappings
                  </Typography>
                  {(node.blanks ?? []).map((blank, blankIdx) => (
                    <Box
                      key={blank.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Chip label={`[[${blank.id}]]`} size="small" />
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Correct Option</InputLabel>
                        <Select
                          value={blank.correct_option_id}
                          label="Correct Option"
                          onChange={(e) =>
                            updateBlank(blankIdx, e.target.value as string)
                          }
                        >
                          {node.options.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.text || opt.id}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                </Box>
              )}

            {/* Explanation */}
            <TextField
              label="Explanation (optional)"
              value={node.explanation ?? ""}
              onChange={(e) =>
                updateField("explanation", e.target.value || undefined)
              }
              fullWidth
              multiline
              rows={2}
              size="small"
              sx={{ mb: 2 }}
              placeholder="e.g., Credit is about trust between borrower and lender"
            />

            {/* Feedback group selectors */}
            <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Correct Feedback</InputLabel>
                <Select
                  value={node.correct_feedback_group_id ?? ""}
                  label="Correct Feedback"
                  onChange={(e) =>
                    updateField(
                      "correct_feedback_group_id",
                      e.target.value || undefined
                    )
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {correctFgs.map((fg) => (
                    <MenuItem key={fg.id} value={fg.id}>
                      {fg.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Incorrect Feedback</InputLabel>
                <Select
                  value={node.incorrect_feedback_group_id ?? ""}
                  label="Incorrect Feedback"
                  onChange={(e) =>
                    updateField(
                      "incorrect_feedback_group_id",
                      e.target.value || undefined
                    )
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {incorrectFgs.map((fg) => (
                    <MenuItem key={fg.id} value={fg.id}>
                      {fg.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};
